/**
 * In-app notifications — the bell's read, its live feed, and mark-read
 * (docs/NOTIFICATIONS.md "Web").
 *
 * Reads and writes go through the Hono backend (`/api/v1/notifications`,
 * apps/api/src/routes/platform/notifications.ts). The ONE direct-client use here is the
 * Realtime subscription: Supabase Realtime has no backend proxy, so the bell
 * (always mounted in the header) owns the single `notifications:<uid>` channel
 * — the second always-mounted subscription after `useInboxUnreadCount`.
 *
 * Liveness contract:
 *   - `INSERT` and `UPDATE` are subscribed BY NAME, never `*`: DELETE events
 *     are not RLS-filtered and carry only the PK (docs/DATA_LAYER.md).
 *   - Under `REPLICA IDENTITY DEFAULT` an UPDATE's `payload.old` is PK-only,
 *     so the unread transition is derived from the CACHED row, never from
 *     `old.read_at` — see `applyNotificationEvent`.
 *   - The subscription is keyed by user (not org) so it survives an org
 *     switch; the reducer filters events to the selected org (+ NULL-org
 *     system rows) when it applies them to that org's cache.
 *   - The bell has two views, each its own cache under the same org prefix:
 *     `unread` (the default — a row leaves the list the moment it is read)
 *     and `all` (the newest rows, read or not). One event is folded into both.
 */
import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { RealtimeChannel } from "@supabase/supabase-js";
import {
  NOTIFICATION_LIST_STATUSES,
  type MarkNotificationsReadInput,
  type MarkNotificationsReadResponse,
  type NotificationListStatus,
  type NotificationWire,
  type NotificationsListResponse,
} from "@releve/shared";
import i18n from "@/i18n";
// eslint-disable-next-line no-restricted-imports -- Realtime is the sanctioned direct-client exception (docs/BACKEND_API.md "What lives where")
import { supabase } from "@/integrations/supabase/client";
import { authedGet, authedPost } from "@/lib/api";
import { addBreadcrumb, captureException } from "@/lib/sentry";
import { toast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";

/** The bell shows the last N rows; the unread count is unbounded server-side. */
const BELL_LIMIT = 30;

/** Prefix for every bell cache of one org — invalidate this to refetch both views. */
export const notificationsKeyPrefix = (orgId: string | undefined) => ["notifications", orgId] as const;

/** Query key for one org's bell feed in one view (`{ items, unread_count }`). */
export const notificationsKey = (orgId: string | undefined, status: NotificationListStatus) =>
  [...notificationsKeyPrefix(orgId), status] as const;

// ---------------------------------------------------------------------------
// Pure reducer — applied to the cache on every Realtime event
// ---------------------------------------------------------------------------

export interface NotificationEvent {
  eventType: "INSERT" | "UPDATE" | "DELETE";
  /** The row after the change. Complete for INSERT/UPDATE (RLS-filtered to the caller's own rows). */
  new: NotificationWire;
}

/**
 * Fold one Realtime event into the cached list for `currentOrgId` in one view.
 *
 * - `INSERT`: bump `unread_count` when unread, and prepend if the row belongs
 *   to this org or is a NULL-org system row, is not already listed (the API
 *   read and the event can race), and — in the `unread` view — is unread.
 * - `UPDATE`: the unread transition comes from the row's PREVIOUS `read_at`
 *   vs the new one (`payload.old` is PK-only) — `prevReadAt` when the caller
 *   found the row in any view's cache, else this cache's copy. The count
 *   moves in every view that knows the previous state, even one whose list
 *   does not hold the row (an unread row can sit in `unread` but be older
 *   than the 30 newest in `all`); the list itself changes only where the row
 *   is present — replaced in `all`, dropped from `unread` once read. A row
 *   known to no cache is the caller's problem (it invalidates). read→unread
 *   is not a write path (the route stamps `read_at` only where NULL), so the
 *   `unread` list is never re-populated by an UPDATE — only its count moves.
 * - `DELETE`: no-op (PK-only, unfiltered; the only deleter is the nightly
 *   retention purge, and a purged row is by definition long read).
 * - No cache yet → stays undefined; the pending/next fetch fills it.
 */
export function applyNotificationEvent(
  cache: NotificationsListResponse | undefined,
  evt: NotificationEvent,
  currentOrgId: string,
  status: NotificationListStatus,
  prevReadAt?: string | null,
): NotificationsListResponse | undefined {
  if (!cache) return undefined;

  if (evt.eventType === "INSERT") {
    const row = evt.new;
    if (row.organization_id !== currentOrgId && row.organization_id !== null) return cache;
    if (cache.items.some((n) => n.id === row.id)) return cache;
    const isUnread = row.read_at === null;
    return {
      items: status === "unread" && !isUnread ? cache.items : [row, ...cache.items].slice(0, BELL_LIMIT),
      unread_count: cache.unread_count + (isUnread ? 1 : 0),
    };
  }

  if (evt.eventType === "UPDATE") {
    const row = evt.new;
    const cached = cache.items.find((n) => n.id === row.id);
    const before = prevReadAt !== undefined ? prevReadAt : cached?.read_at;
    if (before === undefined) return cache;
    let delta = 0;
    if (before === null && row.read_at !== null) delta = -1;
    else if (before !== null && row.read_at === null) delta = 1;
    const dropped = status === "unread" && row.read_at !== null;
    return {
      items: !cached
        ? cache.items
        : dropped
          ? cache.items.filter((n) => n.id !== row.id)
          : cache.items.map((n) => (n.id === row.id ? row : n)),
      unread_count: Math.max(0, cache.unread_count + delta),
    };
  }

  return cache;
}

// ---------------------------------------------------------------------------
// Read + the single Realtime subscription
// ---------------------------------------------------------------------------

/** Matches the normalized transport-drop messages Realtime emits before auto-reconnecting. */
const TRANSIENT_REALTIME_ERROR =
  /^(socket closed: \d+|heartbeat timeout|channel error: (transport failure|connection lost))/;

/**
 * The bell's feed for the selected org in one view (`unread` — the default
 * queue — or `all`): up to 30 rows + the org's unread count. Disabled with no
 * org. Mounts the user-scoped Realtime subscription that keeps BOTH views'
 * caches live (INSERT + UPDATE on the caller's own `notifications` rows); a
 * view switch only changes which query is observed, never the subscription.
 */
export function useNotifications(orgId: string | undefined, status: NotificationListStatus = "unread") {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id;
  const channelRef = useRef<RealtimeChannel | null>(null);
  // The handler reads the CURRENT org so an org switch does not require
  // tearing the user-scoped channel down and up again.
  const orgIdRef = useRef(orgId);
  orgIdRef.current = orgId;
  // One trailing invalidation for a burst of UPDATEs no cache can place
  // (a "Mark all read" is N events; `invalidateQueries` cancels-and-restarts
  // the in-flight fetch on every call, so N calls would be N+1 GETs).
  const invalidateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!userId) return;
    if (channelRef.current) return;

    const channelName = `notifications:${userId}`;

    // Reuse guard (React Strict Mode double-mount / a second header instance):
    // a channel that is `joined` / `joining` has a live owner — let it manage.
    // Any other state is a leftover — `errored` (join failure, socket drop) or
    // `closed` (created, never subscribed); realtime-js closes and removes a
    // channel synchronously on `leave()`, so `leaving` is never seen here.
    // Bailing on a leftover would strand this instance with no channel and no
    // deps change to retry, so remove it first and build once the removal
    // settles.
    const existing = supabase.getChannels().find((ch) => ch.topic === `realtime:${channelName}`);
    if (existing && (existing.state === "joined" || existing.state === "joining")) return;
    let cancelled = false;

    const apply = (evt: NotificationEvent) => {
      const currentOrgId = orgIdRef.current;
      if (!currentOrgId) return;
      // An UPDATE's previous state comes from whichever view's cache holds
      // the row, so both counts move by the same delta (an unread row can be
      // listed in `unread` yet fall outside the 30 newest of `all`). A row
      // no cache knows — a read elsewhere of something older than both
      // windows — is refetched instead of guessed.
      let prevReadAt: string | null | undefined;
      if (evt.eventType === "UPDATE") {
        // Same org rule as the INSERT arm of the reducer: the channel is
        // user-scoped, so another org's row must not cost this org a refetch.
        const oid = evt.new.organization_id;
        if (oid !== currentOrgId && oid !== null) return;
        const prev = NOTIFICATION_LIST_STATUSES.map((view) =>
          queryClient
            .getQueryData<NotificationsListResponse>(notificationsKey(currentOrgId, view))
            ?.items.find((n) => n.id === evt.new.id),
        ).find(Boolean);
        if (!prev) {
          if (!invalidateTimer.current) {
            invalidateTimer.current = setTimeout(() => {
              invalidateTimer.current = null;
              void queryClient.invalidateQueries({ queryKey: notificationsKeyPrefix(currentOrgId) });
            }, 250);
          }
          return;
        }
        prevReadAt = prev.read_at;
      }
      // Both views, even the one not currently shown: a view the user has
      // opened before keeps a cache, and it must not go stale behind the
      // toggle. An updater returning undefined leaves an absent cache absent.
      for (const view of NOTIFICATION_LIST_STATUSES) {
        queryClient.setQueryData<NotificationsListResponse | undefined>(
          notificationsKey(currentOrgId, view),
          (old) => applyNotificationEvent(old, evt, currentOrgId, view, prevReadAt),
        );
      }
    };

    const build = () => {
      const channel = supabase
        .channel(channelName)
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
          (payload) => apply({ eventType: "INSERT", new: payload.new as NotificationWire }),
        )
        .on(
          "postgres_changes",
          { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
          (payload) => apply({ eventType: "UPDATE", new: payload.new as NotificationWire }),
        )
        .subscribe((status, err) => {
          if (!err) return;
          // Transient transport drops (network blip, laptop sleep, proxy idle
          // timeout) arrive as these normalized messages and realtime
          // auto-reconnects, so they are breadcrumbs, not Sentry events. Server
          // join rejections and binding mismatches still get reported.
          if (TRANSIENT_REALTIME_ERROR.test(err.message)) {
            addBreadcrumb(err.message, { status }, "warning");
            return;
          }
          captureException(err instanceof Error ? err : new Error(String(err)), {
            context: "Notifications Realtime Subscription",
            status,
          });
        });

      channelRef.current = channel;
    };

    if (existing) {
      void supabase.removeChannel(existing).then(() => {
        if (!cancelled) build();
      });
    } else {
      build();
    }

    return () => {
      cancelled = true;
      if (invalidateTimer.current) {
        clearTimeout(invalidateTimer.current);
        invalidateTimer.current = null;
      }
      if (channelRef.current) {
        void supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
    // `orgId` is read through a ref; only a user change re-subscribes.
  }, [userId, queryClient]);

  return useQuery({
    queryKey: notificationsKey(orgId, status),
    queryFn: () =>
      authedGet<NotificationsListResponse>(
        `/api/v1/notifications/orgs/${orgId}?limit=${BELL_LIMIT}&status=${status}`,
      ),
    enabled: !!orgId,
    // Realtime keeps the cache live; a refetch is only a safety net.
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Mark read
// ---------------------------------------------------------------------------

/**
 * Patch `read_at` on the cached rows an input names and recompute the count.
 * In the `unread` view a flipped row leaves the list; in `all` it stays, read.
 */
export function markReadInCache(
  cache: NotificationsListResponse | undefined,
  input: MarkNotificationsReadInput,
  now: string,
  status: NotificationListStatus,
): NotificationsListResponse | undefined {
  if (!cache) return undefined;
  const all = "all" in input;
  const ids = new Set(all ? [] : input.ids);
  let flipped = 0;
  const items: NotificationWire[] = [];
  for (const n of cache.items) {
    if (n.read_at !== null || (!all && !ids.has(n.id))) {
      items.push(n);
      continue;
    }
    flipped += 1;
    if (status === "all") items.push({ ...n, read_at: now });
  }
  return {
    items,
    // `unread_count` is not bounded by the list, so "all" zeroes it while an
    // id list only subtracts the rows that actually flipped.
    unread_count: all ? 0 : Math.max(0, cache.unread_count - flipped),
  };
}

/**
 * `POST …/read` with `{ ids }` or `{ all: true }`. Optimistic across both
 * views' caches: they flip first and roll back on error; the org prefix is
 * invalidated on settle so the server's count wins either way.
 */
export function useMarkNotificationsRead(orgId: string | undefined) {
  const queryClient = useQueryClient();
  const prefix = notificationsKeyPrefix(orgId);

  return useMutation({
    mutationFn: (input: MarkNotificationsReadInput) =>
      authedPost<MarkNotificationsReadResponse>(`/api/v1/notifications/orgs/${orgId}/read`, input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: prefix });
      const now = new Date().toISOString();
      const previous = NOTIFICATION_LIST_STATUSES.map((view) => {
        const key = notificationsKey(orgId, view);
        const before = queryClient.getQueryData<NotificationsListResponse>(key);
        queryClient.setQueryData<NotificationsListResponse | undefined>(key, (old) =>
          markReadInCache(old, input, now, view),
        );
        return [view, before] as const;
      });
      return { previous };
    },
    onError: (_error, _input, context) => {
      for (const [view, before] of context?.previous ?? []) {
        queryClient.setQueryData(notificationsKey(orgId, view), before);
      }
      toast.error(i18n.t("notifications.markReadFailed"));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: prefix });
    },
  });
}
